import { useMemo } from "react";
import { SupportedTokensDropListWithIconItem, TradePathBySupportedTokens } from "@cede/types";
import { API_ENDPOINT, USE_SUPPORTED_TOKENS_CACHE_TIME, forgeTokenURL } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

type Params = {
  enabled: boolean;
  accountId?: string;
  exchangeId?: string;
  fromToken?: string;
  network?: string;
};

export const useTradePathBySupportedTokens = ({ enabled, accountId, exchangeId, fromToken, network }: Params) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();

  const account = accountId ? hubUser?.exchangeAccounts.find((acc) => acc.id === Number(accountId)) : null;
  const resolvedExchangeId = exchangeId ?? account?.exchangeId;
  const canFetch = enabled && !!fromToken && !!resolvedExchangeId && !!auth.identityToken;

  const {
    data: tradePathBySupportedTokens,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["tradePathBySupportedTokens", accountId, resolvedExchangeId, fromToken, network],
    queryFn: async () => {
      if (!auth.identityToken || !fromToken || !resolvedExchangeId) return null;
      if (accountId && !account) return null;

      const params: Record<string, string> = {
        from: fromToken,
        exchangeId: resolvedExchangeId,
      };
      if (network) {
        params.network = network;
      }

      return apiHelper.getWithAuth(auth, API_ENDPOINT.TRADE_PATH_BY_SUPPORTED_TOKENS, {
        queryParams: params,
      }) as Promise<TradePathBySupportedTokens>;
    },
    enabled: canFetch,
    staleTime: USE_SUPPORTED_TOKENS_CACHE_TIME,
    gcTime: USE_SUPPORTED_TOKENS_CACHE_TIME,
  });

  const droplistSupportedTokens = useMemo<SupportedTokensDropListWithIconItem[]>(() => {
    if (!tradePathBySupportedTokens) return [];
    return Object.keys(tradePathBySupportedTokens).map<SupportedTokensDropListWithIconItem>((token) => ({
      label: token,
      value: token,
      searchableTerms: [token],
      img: forgeTokenURL(token),
      isDisabled: false,
      networks: [], // useless for the moment, since we assume that getTradePathBySupportedTokens returns the supported tokens for the given network
    }));
  }, [tradePathBySupportedTokens]);

  return {
    tradePathBySupportedTokens,
    supportedTokens: tradePathBySupportedTokens,
    droplistSupportedTokens,
    isLoading: isFetching,
    error,
  };
};
